import { useAccess } from '../../hooks/useAccess'
import { measurementRun } from '../../lib/measurementView'
import { MeasurementProvider } from '../../context/MeasurementContext'
import { Measure } from '../../components/Measurement'
import { extractUnloadedItems } from '../../lib/unloadedItemsPresentation'
import { getFloorPressureStatus } from '../../lib/floorPressureDiagnostics'
import { useState, useMemo, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Pencil, Expand, Minimize, PanelRightClose, PanelRightOpen, HelpCircle, RotateCcw, Box, Cuboid, Scale, ShieldCheck, TriangleAlert, Truck, Send } from 'lucide-react'
import { useOptimizationRunLive } from '../../hooks/useOptimizationRunLive'
import { jobProjectApi } from '../../services/jobProjectApi'
import { optimizationApi } from '../../services/optimizationApi'
import { useOptimizationExport, type OptimizationExportFormat, type OptimizationPdfTemplate, type OptimizationPdfPresentation } from '../../hooks/useOptimizationExport'
import { useLoadingPlayback } from '../../hooks/useLoadingPlayback'
const Optimization3DViewer = lazy(() => import('../../components/optimization/Optimization3DViewer'))
const ManualPlanEditor = lazy(() => import('../../components/optimization/ManualPlanEditor'))
import Dialog from '../../components/ui/Dialog'
import { ViewerMetric, ViewerExportActions, ViewerSceneBoundary, viewerButton } from '../../components/optimization/ViewerDashboard'
import OverlayManager from '../../components/optimization/OverlayManager'
import PublicationPanel from '../../components/optimization/PublicationPanel'
import { buildArticlePresentation, computeFillRateWeightPct, computeLoadedCartonsCount, computeTotalCartonsCount } from '../../lib/optimizationUtils'
import { useTheme } from '../../context/ThemeContext'
import { getVehicleWarnings } from '../../lib/vehicleDiagnostics'
import { outputSummaryViewerRun } from '../../lib/outputSummaryViewerRun'
import { presentCargoLines, palletDisplayLabels } from '../../lib/cargoLinePresentation'
import { withCargoSceneLabels } from '../../lib/viewerCargo'
import type { ManualEditorView } from '../../types/manualPlan'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPlacementPosition(placement: any) {
  return {
    x: Number(placement?.x ?? placement?.position_x ?? placement?.position?.x ?? 0),
    y: Number(placement?.y ?? placement?.position_y ?? placement?.position?.y ?? 0),
    z: Number(placement?.z ?? placement?.position_z ?? placement?.position?.z ?? 0),
  }
}

function sortPlacementsForLoading(placements: any[]) {
  return [...placements].sort((a, b) => {
    const orderA = Number(a.loading_order ?? a.loadingOrder)
    const orderB = Number(b.loading_order ?? b.loadingOrder)
    if (Number.isFinite(orderA) && Number.isFinite(orderB) && orderA !== orderB) return orderA - orderB
    if (Number.isFinite(orderA) && !Number.isFinite(orderB)) return -1
    if (!Number.isFinite(orderA) && Number.isFinite(orderB)) return 1

    const pa = getPlacementPosition(a)
    const pb = getPlacementPosition(b)
    if (pa.x !== pb.x) return pa.x - pb.x
    if (pa.z !== pb.z) return pa.z - pb.z
    if (pa.y !== pb.y) return pa.y - pb.y
    return String(a.item_reference || a.item_id || a.itemId || a.id || '')
      .localeCompare(String(b.item_reference || b.item_id || b.itemId || b.id || ''), undefined, { numeric: true })
  })
}

const GROUPED_WARNING_TYPES = new Set(['load_path_moment_unverified', 'floor_reaction_eccentric'])

function countWarningsForDisplay(warnings: any[]) {
  const groupedTypesSeen = new Set<string>()
  let count = 0

  for (const warning of warnings || []) {
    const type = typeof warning === 'object' && warning ? warning.type : null
    if (type && GROUPED_WARNING_TYPES.has(type)) {
      groupedTypesSeen.add(type)
    } else {
      count += 1
    }
  }

  return count + groupedTypesSeen.size
}

function formatRunTimestamp(value?: string | null) {
  if (!value) return 'Date non disponible'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Date non disponible'
  return date.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// ─── Loading state ──────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div className="w-full h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col items-center justify-center gap-4">
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
          <Truck className="w-8 h-8 text-orange-500 animate-pulse" />
        </div>
        <div className="absolute inset-0 rounded-2xl border-2 border-orange-500/30 animate-ping" />
      </div>
      <div className="text-center">
        <p className="font-semibold">Chargement du plan 3D…</p>
        <p className="text-slate-500 text-sm mt-1">Reconstruction des placements en cours</p>
      </div>
    </div>
  )
}

// ─── Error state ────────────────────────────────────────────────────────────

function ErrorScreen({ message, onBack }: { message: string; onBack: () => void }) {
  return (
    <div className="w-full h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col items-center justify-center gap-4">
      <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
        <TriangleAlert className="w-7 h-7 text-red-400" />
      </div>
      <div className="text-center">
        <p className="font-semibold">Impossible d'afficher le plan</p>
        <p className="text-slate-400 text-sm mt-1">{message}</p>
      </div>
      <button onClick={onBack} className="mt-2 px-5 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold transition-colors">
        Retour aux résultats
      </button>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function OptimizationViewer() {
  const access = useAccess()
  const rights = access.resource('optimization')
  // Export/download routes require an active subscription, but only read rights.
  const canWrite = rights.read && access.subscription.canWrite
  const canPublish = rights.manage
  const { runId } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [fullscreen, setFullscreen] = useState(false)
  const [manualEditing, setManualEditing] = useState(false)
  const [manualScene, setManualScene] = useState<ManualEditorView | null>(null)
  const closeManual = useCallback(() => { setManualEditing(false); setManualScene(null) }, [])
  const savedManual = useCallback((id: string) => { setManualEditing(false); setManualScene(null); navigate(`/optimization/${id}/viewer`) }, [navigate])
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [focusedItemIds, setFocusedItemIds] = useState<string[]>([])
  const [view, setView] = useState<'default' | 'top' | 'front' | 'side'>('default')
  const [searchQuery, setSearchQuery] = useState('')
  const [showRealCG, setShowRealCG] = useState(true)
  const [showTargetCG, setShowTargetCG] = useState(false)
  const [showCargoLabels, setShowCargoLabels] = useState(false)
  const [articlesTab, setArticlesTab] = useState<'items' | 'pallets'>('items')
  const [selectedPressureZone, setSelectedPressureZone] = useState<{
    x: number; y: number; width: number; length: number; itemIds: string[]
  } | null>(null)
  const [selectedRecommendation, setSelectedRecommendation] = useState<any>(null)
  const dashboardRef = useRef<HTMLDivElement>(null)
  const [sceneOnly, setSceneOnly] = useState(false)
  const [publicationPanelOpen, setPublicationPanelOpen] = useState(false)
  const [diagnosticsPanelRequest, setDiagnosticsPanelRequest] = useState(0)
  const [viewerHelpOpen, setViewerHelpOpen] = useState(false)
  const [openWarningsPanel, setOpenWarningsPanel] = useState(0)
  const [violationsPanelRequest, setViolationsPanelRequest] = useState(0)
  const [unloadedPanelRequest, setUnloadedPanelRequest] = useState(0)
  const [pressurePanelRequest, setPressurePanelRequest] = useState(0)

  const resetViewerFilters = useCallback(() => {
    setFocusedItemIds([])
    setSelectedItemIds([])
    setSelectedPressureZone(null)
    setSelectedRecommendation(null)
  }, [])

  const toggleFullscreen = useCallback(async () => {
    if (document.fullscreenElement) { await document.exitFullscreen(); return }
    if (fullscreen) { setFullscreen(false); return }
    try { await dashboardRef.current?.requestFullscreen(); setFullscreen(true) }
    catch { setFullscreen(true) }
  }, [fullscreen])
  useEffect(() => {
    const sync = () => setFullscreen(!!document.fullscreenElement && document.fullscreenElement === dashboardRef.current)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented || document.querySelector('dialog[open]') || (event.target as HTMLElement)?.closest('input, textarea, select, [contenteditable="true"]')) return
      if (event.key === 'Escape') {
        if (fullscreen) { if (document.fullscreenElement) void document.exitFullscreen(); else setFullscreen(false); return }
        if (!manualEditing) resetViewerFilters()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [fullscreen, resetViewerFilters, manualEditing])

  const { run: sourceRun, outputSummary: sourceSummary, outputSummaryError, loading, error } = useOptimizationRunLive({ runId: runId ?? '', enabled: !!runId })
  const { exportRun, isExporting, exportError } = useOptimizationExport(runId ?? '')
  const downloadExport = (format: OptimizationExportFormat, template?: OptimizationPdfTemplate, presentation?: OptimizationPdfPresentation) => {
    if (canWrite) void exportRun(format, template, presentation)
  }

  const cargoPresentation = useMemo(() => sourceRun ? presentCargoLines(measurementRun(sourceRun), sourceSummary) : null, [sourceRun, sourceSummary])
  const run = cargoPresentation?.run
  const outputSummary = cargoPresentation?.summary ?? sourceSummary
  useEffect(() => { setFocusedItemIds([]) }, [runId])
  const projectId = run?.job_id ?? run?.jobId ?? outputSummary?.identity.job_id
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => jobProjectApi.getProject(projectId!),
    enabled: Boolean(projectId),
    staleTime: 60_000,
  })
  const { data: publication } = useQuery({
    queryKey: ['optimization-publication', runId],
    queryFn: () => optimizationApi.getPublication(runId!),
    enabled: Boolean(runId),
    staleTime: 30_000,
  })
  // The operational scene is deliberately projected from the same backend
  // contract used by the exports. Legacy raw data remains a read-only fallback.
  const contractViewerRun = useMemo(() => outputSummary ? outputSummaryViewerRun(outputSummary) : null, [outputSummary])

  const placements = useMemo(() => {
    // A provided sequence is already ordered by the backend contract. For an
    // estimated sequence we intentionally preserve its snapshot order: it is
    // only a display aid and must not be mistaken for a new operational rule.
    if (contractViewerRun) return contractViewerRun.result?.placements || []
    return sortPlacementsForLoading(run?.result?.placements || [])
  }, [contractViewerRun, run?.result?.placements])

  const playbackRun = useMemo(() => {
    if (!run?.result) return run
    return {
      ...run,
      result: {
        ...run.result,
        placements,
      },
    }
  }, [run, placements])

  const sceneRun = useMemo(() => {
    const scene = contractViewerRun ?? playbackRun ?? run
    return scene ? withCargoSceneLabels(scene, run?.input.items) : scene
  }, [contractViewerRun, playbackRun, run])

  const playback = useLoadingPlayback({
    totalSteps: placements.length,
    baseIntervalMs: 600,
    initialStep: loading || !run?.result ? undefined : placements.length,
  })

  const itemMap = useMemo(() => new Map<string, any>(run?.input?.items?.map((item: any) => [item.id, item]) || []), [run?.input?.items])
  const playbackPalletLabels = useMemo(() => run ? palletDisplayLabels(run) : new Map<string, string>(), [run])
  const legacyArticles = useMemo(() => run ? buildArticlePresentation(run) : null, [run])
  const unloadedItems = useMemo(() => run ? extractUnloadedItems(run) : [], [run])
  const currentItemLabel = useMemo(() => {
    if (playback.step === 0 || playback.step > placements.length) return undefined
    const p = placements[playback.step - 1]
    if (!p) return undefined
    const pId = p.item_id || p.itemId || ''
    if (playbackPalletLabels.has(pId)) return playbackPalletLabels.get(pId)
    let matched = itemMap.get(pId)
    if (!matched && pId.includes('_')) {
      const parts = pId.split('_')
      if (!isNaN(Number(parts[parts.length - 1]))) matched = itemMap.get(parts.slice(0, -1).join('_'))
    }
    if (!matched && (p as any).metadata?.source_id) matched = itemMap.get(String((p as any).metadata.source_id))
    return matched?._viewerLabel || matched?.description || matched?.id || pId || undefined
  }, [playback.step, placements, itemMap, playbackPalletLabels])

  if (!runId) return <ErrorScreen message="Identifiant de plan manquant." onBack={() => navigate('/optimization')} />
  if (loading) return <LoadingScreen />
  if (error || !run || !run.result) {
    return <ErrorScreen message={error?.message || 'Résultat indisponible.'} onBack={() => navigate(`/optimization/${runId}`)} />
  }

  // ── Computed values ─────────────────────────────────────────────────────────
  const pressureStatus = getFloorPressureStatus(run)
  const result = run.result
  const validPlacements = placements
  const validPalletGroups = result?.palletGroups ?? result?.pallet_groups ?? []
  const legacyPlacementCounts = legacyArticles?.counts ?? {}
  const validPlacementCounts = outputSummary
    ? Object.fromEntries(outputSummary.cargo.articles.map(article => [article.id, article.loaded ?? 0]))
    : legacyPlacementCounts

  const isPlaybackActive = playback.step < validPlacements.length || playback.isPlaying
  const viewerPlaybackStep = isPlaybackActive ? playback.step : undefined

  const isValid = outputSummaryError ? null : outputSummary
    ? outputSummary.checks.controlled_constraints.status === 'pass' ? true
      : outputSummary.checks.controlled_constraints.status === 'fail' ? false : null
    : result.valid
  const violationCount = outputSummary?.diagnostics.violations_count ?? result.violations?.length ?? 0

  const warnings = outputSummary?.alerts
    ? outputSummary.alerts.warnings.map(warning => ({
      type: warning.type, severity: warning.severity, message: warning.message,
      item_ids: warning.item_ids, parameters: warning.parameters
    }))
    : getVehicleWarnings(result)
  const warningCount = countWarningsForDisplay(warnings)

  const fillRateVol = outputSummaryError ? null : outputSummary
    ? outputSummary.metrics.fill_rate_volume == null ? null : outputSummary.metrics.fill_rate_volume * 100
    : result.fill_rate_volume != null
    ? (result.fill_rate_volume <= 1 ? result.fill_rate_volume * 100 : result.fill_rate_volume)
    : null

  const fillRateSurface = outputSummaryError ? null : outputSummary
    ? outputSummary.metrics.fill_rate_surface == null ? null : outputSummary.metrics.fill_rate_surface * 100
    : result.fill_rate_surface != null
    ? (result.fill_rate_surface <= 1 ? result.fill_rate_surface * 100 : result.fill_rate_surface)
    : null

  const loadedCartonsCount = outputSummary ? outputSummary.cargo.loaded : computeLoadedCartonsCount(run)
  const totalCartonsCount = outputSummary ? outputSummary.cargo.requested : computeTotalCartonsCount(run)
  const unloadedCartonsCount = outputSummary ? outputSummary.cargo.remaining : totalCartonsCount == null || loadedCartonsCount == null ? null : Math.max(0, totalCartonsCount - loadedCartonsCount)
  const loadedRatio = loadedCartonsCount != null && totalCartonsCount != null && totalCartonsCount > 0 ? loadedCartonsCount / totalCartonsCount : null

  const maxWeight = outputSummary?.metrics.max_payload_kg ?? run.input.loadSpace?.maxWeight ?? run.input.loadSpace?.max_weight ?? null
  const maxWeightUnit = outputSummary?.metrics.max_payload_kg != null ? 'kg' : (run.input.loadSpace as any)?.weight_unit || 'kg'
  const fillWeight = outputSummaryError ? null : outputSummary
    ? outputSummary.metrics.fill_rate_weight == null ? null : outputSummary.metrics.fill_rate_weight * 100
    : computeFillRateWeightPct(run)

  const centerOfGravity = outputSummaryError ? undefined : outputSummary?.center_of_gravity
  const cg = centerOfGravity?.status === 'verified' ? centerOfGravity.actual_mm : null
  const targetCg = centerOfGravity?.status === 'verified' ? centerOfGravity.target_mm : null
  const shortId = runId.length > 8 ? runId.slice(0, 8).toUpperCase() : runId.toUpperCase()
  const planVersion = outputSummary?.identity.plan_version
    ?? run.plan_version
    ?? result.plan_version
    ?? result.optimization_metadata?.plan_version
    ?? result.optimizationMetadata?.plan_version
  const projectName = project?.name || `Projet de chargement #${shortId}`
  const runTimestamp = formatRunTimestamp(run.completedAt ?? run.startedAt ?? run.requestedAt)
  const publicationButton = publication?.status === 'published'
    ? { label: 'Plan publié', title: 'Consulter le plan publié, ses liens et son dossier quai', color: isDark ? '#34d399' : '#047857' }
    : publication?.status === 'approved'
      ? { label: 'Prêt à publier', title: 'Publier la version approuvée du plan', color: isDark ? '#60a5fa' : '#1d4ed8' }
      : publication?.status === 'review_requested'
        ? { label: 'Plan en revue', title: 'Poursuivre avec l’approbation du plan', color: isDark ? '#fbbf24' : '#b45309' }
        : { label: 'Diffuser le plan', title: 'Démarrer la revue, l’approbation et la publication du plan', color: isDark ? '#cbd5e1' : '#475569' }
  const operationalStatus = (() => {
    if (outputSummaryError) return {
      level: 'review' as const, label: 'Résultat à vérifier',
      title: 'La projection contrôlée du résultat est indisponible : vérifier les données et les unités du plan.', action: null,
    }
    if (isValid === false || violationCount > 0 || pressureStatus.assessment === 'exceeded') return {
      level: 'blocked' as const, label: `Chargement bloqué${violationCount > 0 ? ` · ${violationCount} violation${violationCount > 1 ? 's' : ''}` : ''}`,
      title: 'Des contraintes bloquantes ne sont pas satisfaites : ce plan ne doit pas être exécuté.', action: 'violations' as const,
    }
    if (isValid == null || pressureStatus.assessment === 'unverified' || outputSummary?.data_quality.status === 'attention') return {
      level: 'review' as const, label: 'Validation requise',
      title: 'Des contrôles ou des données nécessaires à la validation restent à vérifier.', action: pressureStatus.assessment === 'unverified' ? 'pressure' as const : 'diagnostics' as const,
    }
    if (warnings.length > 0) return {
      level: 'warning' as const, label: 'Conforme avec alertes',
      title: 'Les contraintes bloquantes sont satisfaites, mais des avertissements doivent être examinés.', action: 'warnings' as const,
    }
    return {
      level: 'compliant' as const, label: 'Conforme',
      title: 'Toutes les contraintes contrôlées sont satisfaites.', action: null,
    }
  })()

  const openOperationalStatus = () => {
    if (operationalStatus.action === 'violations') {
      setViolationsPanelRequest(value => value + 1)
    } else if (operationalStatus.action === 'warnings') {
      setOpenWarningsPanel(value => value + 1)
    } else if (operationalStatus.action === 'pressure') {
      setPressurePanelRequest(value => value + 1)
    } else {
      setDiagnosticsPanelRequest(value => value + 1)
    }
    setSceneOnly(false)
  }

  const pct = (value: number | null) => value == null ? '—' : `${value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`
  const clearSelection = () => { resetViewerFilters(); playback.seek(validPlacements.length) }
  const scene = <>
    <div className="viewer-stage-toolbar">
      <div className="flex items-center gap-2"><span className="viewer-scene-icon"><Cuboid size={18} /></span><div><h2 className="text-sm font-semibold">Plan de chargement</h2><p className="text-[11px] text-slate-500 dark:text-slate-400">{manualEditing ? 'Édition du chargement' : 'Explorer en 3D'}</p></div><label className="ml-2 inline-flex min-h-10 cursor-pointer items-center gap-2 text-xs text-slate-500 dark:text-slate-400"><input type="checkbox" className="accent-orange-500" checked={showCargoLabels} onChange={event => setShowCargoLabels(event.target.checked)} />Références des colis</label></div>
      <div className="flex flex-wrap items-center gap-1.5">
        <div className="viewer-camera-views" role="group" aria-label="Point de vue 3D">
          {([['default', '3D'], ['top', 'Dessus'], ['front', 'Avant'], ['side', 'Côté']] as const).map(([id, label]) => <button key={id} aria-pressed={view === id} onClick={() => setView(id)}>{label}</button>)}
        </div>
        <button className={viewerButton} aria-label="Aide à la navigation 3D" title="Aide à la navigation 3D" onClick={() => setViewerHelpOpen(true)}><HelpCircle size={17} /></button>
        <button className={viewerButton} aria-label={sceneOnly ? 'Afficher les contrôles' : 'Agrandir la scène'} title={sceneOnly ? 'Afficher les contrôles' : 'Agrandir la scène'} aria-pressed={sceneOnly} onClick={() => setSceneOnly(value => !value)}>{sceneOnly ? <PanelRightOpen size={17} /> : <PanelRightClose size={17} />}</button>
        <button className={viewerButton} aria-label={fullscreen ? 'Quitter le plein écran' : 'Plein écran'} title={fullscreen ? 'Quitter le plein écran' : 'Plein écran'} onClick={() => void toggleFullscreen()}>{fullscreen ? <Minimize size={17} /> : <Expand size={17} />}</button>
      </div>
    </div>
    {(selectedItemIds.length > 0 || focusedItemIds.length > 0 || selectedPressureZone || selectedRecommendation) && <div className="viewer-selection-bar" style={{ visibility: manualEditing ? 'hidden' : 'visible' }}><span>{selectedPressureZone ? 'Zone de pression isolée' : selectedRecommendation ? 'Recommandation affichée' : `${focusedItemIds.length || selectedItemIds.length} élément(s) isolé(s)`}</span><button className="inline-flex items-center gap-1 font-semibold" onClick={clearSelection}><RotateCcw size={13} />Tout afficher</button></div>}
    <div className="viewer-canvas"><ViewerSceneBoundary key={runId}><Suspense fallback={<div role="status" className="flex h-full items-center justify-center gap-2 text-sm text-slate-500"><Cuboid size={20} className="animate-pulse" />Préparation de la scène 3D…</div>}>
      <Optimization3DViewer run={manualEditing && manualScene ? manualScene.run : sceneRun || run} outputEquipment={manualEditing && manualScene ? manualScene.equipment : outputSummaryError ? undefined : outputSummary?.equipment} centerOfGravity={manualEditing ? undefined : centerOfGravity}
        editing={manualEditing ? manualScene?.editing : undefined}
        selectedItemIds={manualEditing ? manualScene?.editing.selectedId ? [manualScene.editing.selectedId] : [] : selectedItemIds} focusedItemIds={manualEditing ? undefined : focusedItemIds} playbackStep={manualEditing || focusedItemIds.length ? undefined : viewerPlaybackStep}
        cameraResetKey={focusedItemIds}
        selectedPressureZone={manualEditing ? null : selectedPressureZone} selectedRecommendation={manualEditing ? null : selectedRecommendation} currentView={view} showCargoLabels={showCargoLabels} showRealCG={!manualEditing && showRealCG} showTargetCG={!manualEditing && showTargetCG} />
    </Suspense></ViewerSceneBoundary>{manualEditing && manualScene?.feedback}</div>
  </>
  return <MeasurementProvider system={run.input?.measurement_system}>
    <div ref={dashboardRef} data-e2e={manualEditing ? 'manual-plan-editor' : undefined} className={`viewer-dashboard ${manualEditing ? 'manual-editor viewer-editing' : ''} ${fullscreen ? 'viewer-dashboard-fullscreen' : ''}`}>
      <div className="viewer-dashboard-inner">
        <header className="viewer-header">
          <div className="flex min-w-0 items-start gap-3">
            <button className={`${viewerButton} mt-0.5 shrink-0`} aria-label="Retour aux projets de chargement" title="Retour aux projets" onClick={() => navigate('/optimization')}><ArrowLeft size={18} /></button>
            <div className="min-w-0"><p className="viewer-eyebrow">Résultat du chargement</p><h1 className="mt-0.5 break-words text-xl font-semibold tracking-tight">{projectName}</h1><div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400"><span>{project?.reference || shortId}</span><span aria-hidden="true">·</span><span>{planVersion != null ? `Version ${planVersion}` : 'Version non renseignée'}</span><span aria-hidden="true">·</span><time>{runTimestamp}</time></div></div>
          </div>
          <div className="viewer-header-actions">
            {manualEditing ? <><span className="viewer-edit-badge">Mode édition</span>{manualScene?.closeButton ?? <button className={viewerButton} onClick={closeManual}>Annuler l’édition</button>}</> : <button className={viewerButton} disabled={!rights.write || !access.subscription.canWrite || sourceRun?.status !== 'completed'} title="Activer l’édition des colis dans cette scène" onClick={() => { playback.pause(); setManualEditing(true) }}><Cuboid size={16} />Ajuster le plan</button>}
            <button className={viewerButton} disabled={manualEditing || !rights.write || !access.subscription.canWrite} title="Modifier la préparation et calculer une nouvelle version" onClick={() => navigate(`/optimization/${runId}`, { state: { initialTab: 'cargaison', editMode: true } })}><Pencil size={15} /><span>Modifier</span></button>
            <ViewerExportActions disabled={manualEditing || !canWrite} busy={isExporting} units={run.input.measurement_system === 'imperial' ? 'imperial' : 'metric'} onExport={downloadExport} />
            <button className={`${viewerButton} viewer-button-primary`} disabled={manualEditing || !canPublish} title={canPublish ? publicationButton.title : 'Droits de gestion requis pour diffuser le plan'} onClick={() => setPublicationPanelOpen(true)}><Send size={16} />{publicationButton.label}</button>
          </div>
        </header>
        {manualEditing && rights.write && access.subscription.canWrite && <Suspense fallback={<p role="status" className="viewer-notice">Préparation du mode édition…</p>}><ManualPlanEditor runId={runId} onClose={closeManual} onSaved={savedManual} onSceneChange={setManualScene} initialSelection={focusedItemIds[0] ?? selectedItemIds[0]} /></Suspense>}
        {manualEditing && manualScene?.status}
        {manualEditing && <p className="text-xs text-slate-500">Les indicateurs ci-dessous décrivent la version enregistrée. Le brouillon est contrôlé séparément pendant l’édition.</p>}
        {exportError && <div className="viewer-notice viewer-notice-danger" role="alert">{exportError}</div>}
        {outputSummaryError && <div className="viewer-notice viewer-notice-warning" role="alert"><TriangleAlert size={17} /><span>La synthèse contrôlée est indisponible. Vérifiez le résultat avant de diffuser ce plan.</span></div>}
        <div className="viewer-metrics">
          <ViewerMetric label="Unités chargées" icon={<Box size={18} />} value={<>{loadedCartonsCount ?? '—'} <span className="text-base font-normal text-slate-400">/ {totalCartonsCount ?? '—'}</span></>} detail={unloadedCartonsCount == null || loadedCartonsCount == null || totalCartonsCount == null ? 'Quantité restante non disponible' : totalCartonsCount === 0 ? 'Aucune unité demandée' : unloadedCartonsCount > 0 ? `${unloadedCartonsCount} unité(s) à traiter` : 'Toutes les unités sont chargées'} progress={loadedRatio == null ? null : loadedRatio * 100} tone={unloadedCartonsCount && unloadedCartonsCount > 0 ? 'warning' : 'neutral'}>
            {unloadedCartonsCount != null && unloadedCartonsCount > 0 && <button className="viewer-text-action" disabled={manualEditing} aria-label="Chargement partiel : voir les non chargés" onClick={() => { setSceneOnly(false); setUnloadedPanelRequest(value => value + 1) }}>Voir les non chargés →</button>}
          </ViewerMetric>
          <ViewerMetric label="Volume utilisé" icon={<Cuboid size={18} />} value={pct(fillRateVol)} detail={fillRateSurface == null ? 'Occupation du sol non disponible' : `${pct(fillRateSurface)} de la surface au sol`} progress={fillRateVol}>
            <button className="viewer-text-action" disabled={manualEditing} onClick={() => { setSceneOnly(false); setPressurePanelRequest(value => value + 1) }}>Analyser la pression au sol →</button>
          </ViewerMetric>
          <ViewerMetric label="Charge utile utilisée" icon={<Scale size={18} />} value={pct(fillWeight)} detail={maxWeight == null ? 'Charge utile non renseignée' : <>Capacité : <Measure value={maxWeight} from={maxWeightUnit} quantity="mass" /></>} progress={fillWeight} tone={fillWeight != null && fillWeight > 100 ? 'danger' : 'neutral'} />
          <ViewerMetric label="État des contrôles" icon={operationalStatus.level === 'compliant' ? <ShieldCheck size={18} /> : <TriangleAlert size={18} />} value={<span className="text-base leading-snug">{operationalStatus.label}</span>} detail={outputSummaryError ? 'Contrôles à vérifier' : `${violationCount} violation(s) · ${warningCount} avertissement(s)`} tone={operationalStatus.level === 'blocked' ? 'danger' : operationalStatus.level === 'compliant' ? 'success' : 'warning'}>
            <button className="viewer-text-action" disabled={manualEditing} title={operationalStatus.title} onClick={openOperationalStatus}>Examiner les contrôles →</button>
          </ViewerMetric>
        </div>
            <OverlayManager
              presentation="dashboard" scene={scene} sceneOnly={sceneOnly} diagnosticsPanelRequest={diagnosticsPanelRequest}
              editing={manualEditing} editorToolbar={manualScene?.toolbar} editorPanel={manualScene?.inspector ?? <p role="status" className="p-4 text-sm">Préparation des outils d’édition…</p>}
              run={run}
              viewerRun={sceneRun || run}
              onLocateItems={ids => {
                playback.pause()
                playback.seek(validPlacements.length)
                setSelectedItemIds([])
                setSelectedPressureZone(null)
                setSelectedRecommendation(null)
                setView('default')
                setFocusedItemIds(ids)
              }}
              outputSummary={outputSummary}
              outputSummaryUnavailable={!!outputSummaryError}
              fullscreen={fullscreen}
              runId={runId}
              navigate={navigate}
              isDark={isDark}
              cg={cg}
              targetCg={targetCg}
              showRealCG={showRealCG}
              showTargetCG={showTargetCG}
              onShowRealCGChange={setShowRealCG}
              onShowTargetCGChange={setShowTargetCG}
              fillRateWeight={fillWeight}
              currentView={view}
              onViewChange={setView}
              unloadedItems={unloadedItems}
              playback={{
                step: playback.step,
                totalSteps: playback.totalSteps,
                isPlaying: playback.isPlaying,
                speed: playback.speed,
                isAtEnd: playback.isAtEnd,
                isAtStart: playback.isAtStart,
                progress: playback.progress,
                currentItemLabel,
                onPlay: playback.play,
                onPause: playback.pause,
                onReset: playback.reset,
                onNext: playback.nextStep,
                onPrev: playback.prevStep,
                onSpeedChange: playback.setSpeed,
                onSeek: playback.seek,
              }}
              onCellClick={(cell: any) => {
                if (!cell || !cell.items || cell.items.length === 0) {
                  setSelectedPressureZone(null)
                } else {
                  setSelectedPressureZone({
                    x: cell.x,
                    y: cell.y,
                    width: cell.width,
                    length: cell.length,
                    itemIds: cell.items.map((i: any) => i.itemId).filter(Boolean),
                  })
                }
              }}
              onHotSpotClick={(hs: any) => {
                if (!hs || !hs.cell || !hs.responsibleItems || hs.responsibleItems.length === 0) {
                  setSelectedPressureZone(null)
                } else {
                  setSelectedPressureZone({
                    x: hs.cell.x,
                    y: hs.cell.y,
                    width: hs.cell.width,
                    length: hs.cell.length,
                    itemIds: hs.responsibleItems.map((i: any) => i.itemId).filter(Boolean),
                  })
                }
              }}
              selectedPressureZone={selectedPressureZone}
              setSelectedPressureZone={setSelectedPressureZone}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              articlesTab={articlesTab}
              setArticlesTab={setArticlesTab}
              validPlacementCounts={validPlacementCounts}
              validPalletGroups={validPalletGroups}
              selectedItemIds={selectedItemIds}
              setSelectedItemIds={ids => {
                setFocusedItemIds([])
                setSelectedPressureZone(null)
                setSelectedRecommendation(null)
                setSelectedItemIds(ids)
              }}
              selectedRecommendation={selectedRecommendation}
              setSelectedRecommendation={setSelectedRecommendation}
              openWarningsPanel={openWarningsPanel}
              pressurePanelRequest={pressurePanelRequest}
              violationsPanelRequest={violationsPanelRequest}
              unloadedPanelRequest={unloadedPanelRequest}
            />
        {manualEditing && manualScene?.footer}
        {manualEditing && manualScene?.dialogs}
      </div>
      <Dialog isOpen={viewerHelpOpen} onClose={() => setViewerHelpOpen(false)} title="Explorer le plan 3D">
        <div className="space-y-4 text-sm"><p>Choisissez une vue pour retrouver rapidement vos repères.</p><dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-3"><dt className="font-semibold">Rotation</dt><dd>Glissez avec le bouton gauche ou avec un doigt.</dd><dt className="font-semibold">Zoom</dt><dd>Utilisez la molette ou pincez avec deux doigts.</dd><dt className="font-semibold">Déplacement</dt><dd>Glissez avec le bouton droit ou avec deux doigts.</dd><dt className="font-semibold">Inspection</dt><dd>Sélectionnez un article dans le panneau de contrôle pour l’isoler.</dd><dt className="font-semibold">Tout afficher</dt><dd>Utilisez le bouton de réinitialisation ou la touche Échap.</dd></dl></div>
      </Dialog>
      {publicationPanelOpen && canPublish && <PublicationPanel runId={runId} onClose={() => setPublicationPanelOpen(false)} />}
    </div>
  </MeasurementProvider>
}
